import React, { useEffect, useRef, useState } from "react";
import BottomSheet from "./BottomSheet";
import { usersApi } from "../services/users";
import { formatTxnAmount } from "../utils/currency";
import { minCashoutFor } from "../utils/payinConfig";
import type { ReferralAddReason, ReferralAddResponse, ReferralCashout, ReferralCashoutCurrency } from "../types/referrals";
import { t, formatLocale } from "../i18n";
import { T } from "../i18n/T";
import "./ReferralCashout.css";

// Refer & Earn → "Add to my balance" (founder design 10/22, round-2 option A).
// The sheet opens exactly as before; tapping "Total earned" reveals this card
// above the per-person list: what is ready, the green button, what unlocks
// next. The button opens a confirm sheet, then a success state. Every refusal
// is one calm line — never an error-looking screen. No `cashout` block from
// the server (feature off) = no card at all: the reveal is today's list.
//
// Money safety on this side: the confirm button disables on the first tap and
// a ref blocks a second request in flight; each tap sends a fresh requestKey,
// and a retry after NO answer (offline, a 5xx) re-sends the SAME key, so the
// server answers it from the first try's rows and never moves money twice.

/** "$1-class" (founder, 10/22): a fifth of the $5-class cash-out minimum as it
    stood then, in each currency's own round number. Frozen here: the cash-out
    minimums moved up with the price tiers (#561, 10/30); this one did not. */
const REFERRAL_ADD_MIN: Record<string, number> = { usd: 1, eur: 1, gbp: 1, cad: 1, chf: 1, sek: 10, dkk: 10, nok: 10, pln: 4, czk: 20, huf: 400, ron: 5 };
export const minReferralAddFor = (cur: string) => REFERRAL_ADD_MIN[String(cur || "usd").toLowerCase()] ?? minCashoutFor(cur) / 5;

/** 8–64 of A-Z a-z 0-9 _ - (server ADD_BODY). */
export function newRequestKey(): string {
  const rnd = Array.from({ length: 4 }, () => Math.floor(Math.random() * 0x100000000).toString(36)).join("");
  return `rk_${Date.now().toString(36)}_${rnd}`.slice(0, 64);
}

const money = (amount: number, cur: string) => formatTxnAmount(amount, cur);
const onDay = (iso: string) => new Date(iso).toLocaleDateString(formatLocale(), { month: "short", day: "numeric" });

const svg = (d: React.ReactNode, size: number, sw = 2) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const COINS_D = (
  <>
    <ellipse cx="12" cy="6" rx="6.5" ry="2.6" />
    <path d="M5.5 6v4c0 1.4 2.9 2.6 6.5 2.6s6.5-1.2 6.5-2.6V6" />
    <path d="M5.5 10v4c0 1.4 2.9 2.6 6.5 2.6s6.5-1.2 6.5-2.6v-4" />
    <path d="M5.5 14v4c0 1.4 2.9 2.6 6.5 2.6s6.5-1.2 6.5-2.6v-4" />
  </>
);
const CLOCK = svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>, 14, 2.4);
const PLUS = svg(<path d="M12 5v14M5 12h14" />, 18, 2.6);
const BANK = svg(<><path d="M3 10h18L12 4 3 10Z" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" /></>, 18, 2.2);
const CHECK = (s: number) => svg(<path d="M5 12.5l4.5 4.5L19 7.5" />, s, 3);
export const COINS_ICON = (s: number, sw = 2) => svg(COINS_D, s, sw);

/** The calm line for a refusal that leaves the card in place (no button). */
export function reasonLine(reason: ReferralAddReason): string {
  switch (reason) {
    case "masspay_rail":
      return t("referralCashout.reasonMasspay");
    case "outside_transfer_zone":
      return t("referralCashout.reasonZone");
    case "currency_not_funded":
      return t("referralCashout.reasonCurrency");
    case "account_not_activated":
      return t("referralCashout.reasonApproval");
    case "account_frozen":
      return t("referralCashout.reasonPaused");
    case "in_progress":
      return t("referralCashout.reasonInProgress");
    case "pending_check":
      return t("referralCashout.reasonChecking");
    default:
      return t("referralCashout.reasonUnavailable");
  }
}

/** The line inside the confirm sheet when a tap didn't go through (the sheet stays, the button retries). */
function retryLine(reason: ReferralAddReason | null, status: number | null): string {
  if (status === null) return t("referralCashout.retryOffline");
  switch (reason) {
    case "rate_limited":
      return t("referralCashout.retryRateLimited");
    case "in_progress":
      return t("referralCashout.retryInProgress");
    case "pending_check":
      return t("referralCashout.reasonChecking");
    case "transfer_refused":
      return t("referralCashout.retrySupport");
    case "platform_funds_low":
      return t("referralCashout.retryLater");
    default:
      return t("referralCashout.retryGeneric");
  }
}

// Refusals that describe the ACCOUNT, not this tap: the sheet closes and the
// card shows the matching state (bank → the Connect button, the rest a line).
const CARD_REASONS = new Set([
  "bank_not_connected", "masspay_rail", "outside_transfer_zone", "currency_not_funded", "nothing_ready",
  "account_frozen", "account_not_activated", "account_closed", "account_not_found", "not_eligible",
]);

/** Which currencies get a card: anything ready, unlocking, still in the balance or just added; else one $0 card. */
export function cardCurrencies(cashout: ReferralCashout, fallback: string, justAdded: Record<string, number>): string[] {
  const live = Object.entries(cashout.byCurrency || {})
    .filter(([cur, v]) => v.ready > 0 || v.maturing > 0 || v.inBalance > 0 || justAdded[cur] > 0)
    // Funded first, then the most money.
    .sort(([, a], [, b]) => Number(b.funded) - Number(a.funded) || b.ready + b.maturing - (a.ready + a.maturing))
    .map(([cur]) => cur);
  return live.length ? live : [String(fallback || "usd").toLowerCase()];
}

const EMPTY: ReferralCashoutCurrency = {
  earned: 0, ready: 0, maturing: 0, nextUnlockAt: null, nextUnlockAmount: 0, credited: 0, inBalance: 0, funded: true,
};

type CardProps = {
  cur: string;
  v: ReferralCashoutCurrency;
  reason: ReferralAddReason | null;
  justAdded?: number;
  onAdd: () => void;
  onConnectBank: () => void;
};

/** One currency's "Ready to add" card. */
export function ReferralReadyCard({ cur, v, reason, justAdded, onAdd, onConnectBank }: CardProps) {
  const ready = Math.max(0, Number(v.ready) || 0);
  const min = minReferralAddFor(cur);
  const unfunded = !v.funded && ready > 0;
  const why: ReferralAddReason | null = unfunded ? "currency_not_funded" : reason && reason !== "nothing_ready" ? reason : null;
  const anythingNow = ready > 0 || (justAdded || 0) > 0 || v.inBalance > 0;

  let action: React.ReactNode = null;
  if (justAdded) {
    action = <div className="rcx-done">{CHECK(16)}{t("referralCashout.addedToBalance", { amount: money(justAdded, cur) })}</div>;
  } else if (ready > 0 && why === "bank_not_connected") {
    action = (
      <button type="button" className="rcx-btn rcx-btn--bank" onClick={onConnectBank}>
        {BANK}
        {t("referralCashout.connectBank")}
      </button>
    );
  } else if (ready > 0 && why) {
    action = <div className="rcx-line">{reasonLine(why)}</div>;
  } else if (ready > 0 && Math.round(ready * 100) < Math.round(min * 100)) {
    action = <div className="rcx-line">{t("referralCashout.addFrom", { amount: money(min, cur) })}</div>;
  } else if (ready > 0) {
    action = (
      <button type="button" className="rcx-btn" onClick={onAdd}>
        {PLUS}
        {t("referralCashout.addToBalance")}
      </button>
    );
  } else if (v.inBalance > 0) {
    action = <div className="rcx-done">{CHECK(16)}{t("referralCashout.inBalance", { amount: money(v.inBalance, cur) })}</div>;
  }

  // What unlocks next. nextUnlockAmount is the next night's set only, so it
  // never over-claims the date; anything later reads "on the way".
  const nextAmt = Number(v.nextUnlockAmount) || 0;
  const maturing = Number(v.maturing) || 0;
  const bold = { 0: (c: React.ReactNode) => <b>{c}</b> };
  let next: React.ReactNode = null;
  if (v.nextUnlockAt && nextAmt > 0) {
    next = (
      <T
        k={anythingNow ? "referralCashout.moreUnlocks" : "referralCashout.unlocks"}
        vars={{ amount: money(nextAmt, cur), date: onDay(v.nextUnlockAt) }}
        tags={bold}
      />
    );
  } else if (maturing > 0) {
    next = <T k="referralCashout.moreOnTheWay" vars={{ amount: money(maturing, cur) }} tags={bold} />;
  }

  return (
    <div className="rcx-card" data-cur={cur}>
      <div className="rcx-top">
        <span className="rcx-ic">{COINS_ICON(26)}</span>
        <div className="rcx-txt">
          <div className="rcx-k">{t("referralCashout.readyToAdd")}</div>
          <div className={`rcx-v${ready > 0 ? "" : " is-zero"}`}>{money(ready, cur)}</div>
        </div>
      </div>
      {/* What unlocks next sits ABOVE the button (founder, 1.5.2 b48). */}
      {next && <div className="rcx-mature">{CLOCK}<span>{next}</span></div>}
      {action}
      {!anythingNow && <div className="rcx-note">{t("referralCashout.unlockRule")}</div>}
    </div>
  );
}

type SheetState = {
  cur: string;
  amount: number;
  phase: "confirm" | "busy" | "done";
  line?: string;
};

type Args = {
  /** The Refer & Earn sheet is open; closing it forgets this visit's local states. */
  open: boolean;
  cashout?: ReferralCashout;
  /** The talent's own currency: the $0 card's. */
  currency: string;
  onCashoutChanged?: (next: ReferralCashout | undefined) => void;
  onConnectBank?: () => void;
  onSeeBalance?: () => void;
};

/**
 * The cards (for the revealed panel) and the confirm / success sheet (drawn
 * beside the Refer & Earn sheet, never inside it — a fixed overlay inside an
 * animating sheet can be clipped).
 */
export function useReferralCashout({ open: sheetOpen, cashout, currency, onCashoutChanged, onConnectBank, onSeeBalance }: Args) {
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({});
  // A refusal learned from the POST that describes the account (no bank, …).
  const [localReason, setLocalReason] = useState<ReferralAddReason | null>(null);
  const busy = useRef(false);
  // The key of a tap that got NO answer — the retry re-sends it.
  const retryKey = useRef<string | null>(null);

  const reason = localReason ?? cashout?.reason ?? null;

  // Next visit reads the server's numbers again (a bank connected meanwhile, a cash-out made).
  useEffect(() => {
    if (sheetOpen) return;
    setLocalReason(null);
    setJustAdded({});
  }, [sheetOpen]);

  const open = (cur: string, amount: number) => {
    if (busy.current) return;
    retryKey.current = null;
    setSheet({ cur, amount, phase: "confirm" });
  };

  const close = () => {
    if (busy.current) return;
    setSheet(null);
  };

  const confirm = async () => {
    if (!sheet || busy.current || sheet.phase === "done") return;
    busy.current = true;
    const key = retryKey.current || newRequestKey();
    retryKey.current = key;
    const { cur } = sheet;
    setSheet((s) => (s ? { ...s, phase: "busy", line: undefined } : s));
    let body: ReferralAddResponse | null = null;
    let status: number | null = 200;
    try {
      body = await usersApi.addReferralToBalance(key, cur);
    } catch (e: any) {
      status = typeof e?.response?.status === "number" ? e.response.status : null;
      body = (e?.response?.data as ReferralAddResponse) || null;
    } finally {
      busy.current = false;
    }

    const added = Number(body?.added?.[cur]) || 0;
    if (status === 200 && added > 0) {
      retryKey.current = null;
      setJustAdded((m) => ({ ...m, [cur]: added }));
      setLocalReason(null);
      onCashoutChanged?.(body?.cashout ?? locallyAdded(cashout, cur, added));
      setSheet({ cur, amount: added, phase: "done" });
      return;
    }

    const why = (body?.reason as ReferralAddReason | undefined) ?? null;
    // A definite answer: the next tap is a new tap. No answer (offline, a 5xx
    // without a reason): keep the key so the retry can't add twice.
    const definite = status !== null && !!why;
    if (definite) retryKey.current = null;
    if (body?.cashout) onCashoutChanged?.(body.cashout);

    if (why === "feature_off") {
      setSheet(null);
      onCashoutChanged?.(undefined);
      return;
    }
    if (why && CARD_REASONS.has(why)) {
      if (why !== "nothing_ready") setLocalReason(why);
      setSheet(null);
      return;
    }
    setSheet((s) => (s ? { ...s, phase: "confirm", line: retryLine(why, status) } : s));
  };

  const cards = cashout ? (
    <div className="rcx-cards">
      {cardCurrencies(cashout, currency, justAdded).map((cur) => {
        const v = cashout.byCurrency?.[cur] || EMPTY;
        return (
          <ReferralReadyCard
            key={cur}
            cur={cur}
            v={v}
            reason={reason}
            justAdded={justAdded[cur]}
            onAdd={() => open(cur, v.ready)}
            onConnectBank={() => onConnectBank?.()}
          />
        );
      })}
    </div>
  ) : null;

  const done = sheet?.phase === "done";
  const amountTxt = sheet ? money(sheet.amount, sheet.cur) : "";
  const confirmSheet = (
    <BottomSheet open={!!sheet} plain onClose={close} title={t("referralCashout.addToBalance")} className="rcx-sheet">
      {sheet && (
        <div className={`rcx-sheet-body${done ? " is-done" : ""}`} key={done ? "done" : "confirm"}>
          {done ? (
            <>
              <div className="rcx-hero rcx-hero--ok">{CHECK(42)}</div>
              <h3 className="rcx-h">{t("referralCashout.addedTitle", { amount: amountTxt })}</h3>
              <p className="rcx-p">{t("referralCashout.addedBody")}</p>
              <button type="button" className="rcx-cta" onClick={() => { setSheet(null); onSeeBalance?.(); }}>
                {t("referralCashout.seeBalance")}
              </button>
              <button type="button" className="rcx-ghost" onClick={close}>
                {t("referralCashout.done")}
              </button>
            </>
          ) : (
            <>
              <div className="rcx-hero">{COINS_ICON(40, 1.9)}</div>
              <div className="rcx-amt">
                {amountTxt}
                <small>{t("referralCashout.referralEarnings")}</small>
              </div>
              <p className="rcx-p">{t("referralCashout.confirmBody", { amount: amountTxt })}</p>
              {sheet.line && (
                <p className="rcx-retry" role="status">
                  {sheet.line}
                </p>
              )}
              <button type="button" className="rcx-cta" onClick={confirm} disabled={sheet.phase === "busy"} aria-busy={sheet.phase === "busy"}>
                {sheet.phase === "busy" ? t("referralCashout.adding") : sheet.line ? t("referralCashout.tryAgain") : t("referralCashout.addToBalance")}
              </button>
              <button type="button" className="rcx-ghost" onClick={close} disabled={sheet.phase === "busy"}>
                {t("referralCashout.notNow")}
              </button>
            </>
          )}
        </div>
      )}
    </BottomSheet>
  );

  return { cards, sheet: confirmSheet };
}

/** An older server's 200 without `cashout`: move the added money from ready to inBalance here. */
function locallyAdded(cashout: ReferralCashout | undefined, cur: string, added: number): ReferralCashout | undefined {
  if (!cashout) return cashout;
  const v = cashout.byCurrency?.[cur] || EMPTY;
  return {
    ...cashout,
    byCurrency: {
      ...cashout.byCurrency,
      [cur]: { ...v, ready: Math.max(0, v.ready - added), credited: v.credited + added, inBalance: v.inBalance + added },
    },
  };
}

/**
 * Balance tab: the Refer & Earn money sitting in the balance, not cashed out
 * yet ("Referral bonus", founder 10/22 — a line, never a chip on the card).
 */
export function ReferralBonusRow({ byCurrency }: { byCurrency: Record<string, number> }) {
  const parts = Object.entries(byCurrency).filter(([, v]) => Number(v) > 0);
  if (!parts.length) return null;
  return (
    <div className="rcx-row">
      <span className="rcx-row-ic">{COINS_ICON(20)}</span>
      <span className="rcx-row-main">
        <span className="rcx-row-t">{t("referralCashout.referralBonus")}</span>
        <span className="rcx-row-s">{t("referralCashout.inYourBalance")}</span>
      </span>
      <span className="rcx-row-amt">{parts.map(([cur, v]) => `+${money(Number(v), cur)}`).join(" · ")}</span>
    </div>
  );
}

/** Payout details: the Refer & Earn money a payout carried ("Referral bonus"). */
export function ReferralPayoutLine({ byCurrency }: { byCurrency: Record<string, number> }) {
  const parts = Object.entries(byCurrency).filter(([, v]) => Number(v) > 0);
  if (!parts.length) return null;
  return (
    <div className="rcx-payout-line">
      <span className="rcx-row-ic">{COINS_ICON(18)}</span>
      <span className="rcx-row-main">
        <span className="rcx-row-t">{t("referralCashout.referralBonus")}</span>
      </span>
      <span className="rcx-row-amt">{parts.map(([cur, v]) => `+${money(Number(v), cur)}`).join(" · ")}</span>
    </div>
  );
}

/** A payout's referral money per currency (Payout totalsByCurrency[cur].referralBonus), or null when it carried none. */
export function referralBonusOf(totals?: Record<string, { referralBonus?: number }> | null): Record<string, number> | null {
  const out: Record<string, number> = {};
  for (const [cur, v] of Object.entries(totals || {})) if (Number(v?.referralBonus) > 0) out[cur] = Number(v!.referralBonus);
  return Object.keys(out).length ? out : null;
}
